import { createSelector } from 'reselect';

import type { AssetsCollectionSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import type { SearchSavedSearchResultsSchemaWithId } from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_SAVED_SEARCHES,
} from '@iconik-io/iconik-api/src/types/object_types';
import { COLOR_CODING } from '@iconik-io/shared-lib/src/utils/constants/player';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import { EXTRASMALL_SCREEN } from '../../../../constants/breakpoints';
import {
  COLLECTION_ACLS_PANEL,
  COLLECTION_SHARES_PANEL,
  CUTTING_ROOM_PROJECT_PANEL,
  POSTERS_KEY,
  SEARCH_ASSET_METADATA_PANEL,
  SEARCH_COLLECTIONS_PANEL,
  SEARCH_FILTERS_PANEL,
  SEARCH_HISTORY_PANEL,
  SEARCH_SAVED_SEARCH_ACLS_PANEL,
  SEARCH_SAVED_SEARCHES_PANEL,
  UPLOADS_PANEL,
} from '../../../../constants/data';
import {
  ACLS_HELP,
  COLLECTIONS,
  METADATA,
  SAVED_SEARCHES,
  SEARCH_FILTERS,
  SEARCH_HISTORY,
  SHARES,
} from '../../../../constants/help_urls';
import { ONBOARDING_MODAL } from '../../../../constants/modals';
import {
  COLLECTION_PAGE_ROUTE,
  FAVOURITES_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
  SEARCH_PAGE_ROUTE,
} from '../../../../constants/routes';
import type { AppAppState } from '../../../../ducks/app/types';
import { selectLastNavigationItemSelectedIds } from '../../../../ducks/navigation_history/selectors';
import { selectIsNltfActive } from '../../../../ducks/nltf/selectors';
import {
  reloadSearchItems,
  updateUrlArgs,
} from '../../../../ducks/pages/search/thunks';
import { getRouteCollectionId } from '../../../../ducks/pages/search/utils';
import { setPanelDirtyById } from '../../../../ducks/panels/pristine/slice';
import { searchFilterActions } from '../../../../ducks/panels/search_filters/actions';
import { SEARCH_QUERY_FILTER_KEY } from '../../../../ducks/search/active_filter_chips';
import { selectFiltersCount } from '../../../../ducks/search/selectors';
import {
  getIsCuttingRoomWritable,
  getIsNltfAvailable,
} from '../../../../ducks/settings/select_features';
import { getStorages } from '../../../../ducks/storages/selectors';
import { selectUploads } from '../../../../ducks/uploader/selectors';
import AssetMetadataPanel from '../../../../panels/asset_metadata/asset_metadata';
import CollectionsPanel from '../../../../panels/collections/collections';
import CuttingRoomProjectPanel from '../../../../panels/cutting_room/cutting_room_project';
import EntityAclsPanel from '../../../../panels/entity_acls/entity_acls';
import EntityPostersPanel from '../../../../panels/entity_posters/entity_posters';
import GridSearchFiltersPanel from '../../../../panels/grid_search_filters/grid_search_filters';
import SavedSearchesPanel from '../../../../panels/saved_searches/saved_searches';
import SearchHistoryPanel from '../../../../panels/search_history/search_history';
import SharesPanel from '../../../../panels/shares/shares';
import { getUploadingBadge } from '../../../../panels/uploads/helpers';
import { selectHasRolesToUpload } from '../../../../panels/uploads/selectors';
import { UploadsWidgetPanel } from '../../../../panels/uploads/uploads';
import type { AppDispatch, AppState } from '../../../../store/types';
import { isCollectionMappedToStorage } from '../../../../utils/collections';
import type { PanelItem } from '../../types';
import { PanelsList } from '../../utils/panels_list';
import { getMetadataPanelItem } from '../common';

// The kit's own argument positions are (state, panels, dispatch, entityId,
// versionId), so the uploads selector is adapted rather than composed directly.
const getHasRolesToUpload = (
  state: AppState,
  _panels: unknown,
  _dispatch: unknown,
  entityId: string | undefined,
  versionId: string | undefined,
  route: string,
) => selectHasRolesToUpload(state, entityId, versionId, route);

const getUserRoles = (state: AppState): UserRoles =>
  state.rolesReducer.userRoles;

const getRoute = (
  _state: AppState,
  _panels: PanelItem[] | undefined,
  _dispatch: AppDispatch,
  _entityId: string | undefined,
  _versionId: string | undefined,
  route: string,
) => route;

const getEntityId = (
  _state: AppState,
  _panels: PanelItem[] | undefined,
  _dispatch: AppDispatch,
  entityId: string | undefined,
) => entityId;

const getCollection = (
  state: AppState,
  _panels: PanelItem[] | undefined,
  _dispatch: AppDispatch,
  entityId: string | undefined,
): AssetsCollectionSchemaWithId | undefined =>
  entityId ? state.entities.collections[entityId] : undefined;

const getSavedSearch = (
  state: AppState,
  _panels: PanelItem[] | undefined,
  _dispatch: AppDispatch,
  entityId: string | undefined,
): SearchSavedSearchResultsSchemaWithId | undefined =>
  entityId ? state.entities.saved_search_results[entityId] : undefined;

const getDispatch = (
  _state: AppState,
  _panels: PanelItem[] | undefined,
  dispatch: AppDispatch,
) => dispatch;

const getSmallScreen = (state: AppState) =>
  state.displayReducer.breakpoint <= EXTRASMALL_SCREEN;

const getLockMappedCollection = (state: AppState) =>
  state.settings.merged.lock_mapped_collections || false;
const getApp = (state: AppState): AppAppState => state.appReducer;

const getIsOnboarding = (state: AppState) =>
  state.modalsManager[ONBOARDING_MODAL]?.isOpen;

const getSearchItemEntities = (state: AppState) => state.entities.search_items;

const selectLastSelectedSearchItem = createSelector(
  getSearchItemEntities,
  selectLastNavigationItemSelectedIds,
  (searchItems, lastSelectedSearchItemIds) => {
    const id = lastSelectedSearchItemIds.at(-1);
    if (!id) {
      return undefined;
    }
    return searchItems[id];
  },
);

const getCuttingRoomProjectTitle = (state: AppState): string | null =>
  state.cuttingRoom.projectTitle;

const getSearchFilterQuery = (state: AppState): string => {
  const { query, disabledFilters } = state.panelsState.searchFilters;
  // A disabled query should not count toward the badge.
  return (disabledFilters ?? []).includes(SEARCH_QUERY_FILTER_KEY) ? '' : query;
};

export const selectGridSearchPanels = createSelector(
  getSmallScreen,
  getIsOnboarding,
  getUserRoles,
  getRoute,
  getEntityId,
  getCollection,
  getSavedSearch,
  selectUploads,
  getDispatch,
  getHasRolesToUpload,
  selectFiltersCount,
  getRouteCollectionId,
  getLockMappedCollection,
  selectLastSelectedSearchItem,
  getApp,
  getStorages,
  getIsCuttingRoomWritable,
  getCuttingRoomProjectTitle,
  getIsNltfAvailable,
  selectIsNltfActive,
  getSearchFilterQuery,
  (
    isSmallScreen,
    isOnboarding,
    userRoles,
    route,
    entityId,
    collection,
    savedSearch,
    files,
    dispatch,
    canUpload,
    filtersCount,
    collectionId,
    lockMappedCollections = false,
    lastSelectedSearchItem,
    app,
    storages,
    isCuttingRoomWritable,
    cuttingRoomProjectTitle,
    isNltfEnabled,
    isNltfActive,
    searchFilterQuery,
  ) => {
    const roles = userRoles;
    const panels = new PanelsList();

    const isMappedCollection = isCollectionMappedToStorage(collection);

    const isMappedCollectionLocked =
      isMappedCollection && lockMappedCollections;
    const storage = collection?.storage_id
      ? storages[collection?.storage_id]
      : null;
    const isArchived = storage?.purpose === 'ARCHIVE';

    const hasMetadataPanel =
      userRoles.canReadMetadataCategories &&
      userRoles.canReadMetadataViews &&
      userRoles.canReadMetadataFields &&
      userRoles.canReadMetadataValues;

    if (roles.canSearch) {
      const showLeftoverQuery = isNltfEnabled && isNltfActive;
      const hasLeftoverQuery =
        showLeftoverQuery && searchFilterQuery.trim().length > 0;
      const appliedFiltersCount = filtersCount + (hasLeftoverQuery ? 1 : 0);

      panels.push({
        title: 'Filters',
        key: SEARCH_FILTERS_PANEL,
        iconName: 'filter',
        component: GridSearchFiltersPanel,
        props: {},
        color: COLOR_CODING.filters,
        helpButton: {
          path: SEARCH_FILTERS,
          title: 'Find what you need fast with filters',
        },
        badge: appliedFiltersCount,
        badgeLabel: 'Applied filters count',
        actions:
          appliedFiltersCount === 0
            ? []
            : [
                {
                  label: 'Reset filters',
                  action: () => {
                    dispatch(searchFilterActions.resetFiltersData());
                    if (showLeftoverQuery) {
                      dispatch(searchFilterActions.setQuery(''));
                      dispatch(updateUrlArgs({ search: '', page: 1 }));
                    }
                    dispatch(reloadSearchItems());
                    if (route === SAVED_SEARCH_PAGE_ROUTE) {
                      dispatch(setPanelDirtyById(SEARCH_FILTERS_PANEL));
                    }
                  },
                },
              ],
      });
    }

    if (roles.canReadCollections) {
      panels.push({
        title: 'Collections',
        key: SEARCH_COLLECTIONS_PANEL,
        iconName: 'collection',
        component: CollectionsPanel,
        props: {
          aclsType: OBJECT_TYPE_COLLECTIONS,
          aclsObjectId: collectionId,
        },
        color: COLOR_CODING.collections,
        helpButton: { path: COLLECTIONS, title: 'Collections' },
      });
    }

    if (roles.canReadSavedSearches) {
      panels.push({
        title: 'Saved Searches',
        key: SEARCH_SAVED_SEARCHES_PANEL,
        iconName: 'saved_search',
        component: SavedSearchesPanel,
        props: {},
        color: COLOR_CODING.saved_searches,
        helpButton: { path: SAVED_SEARCHES, title: 'Saved Search' },
      });
    }

    switch (route) {
      case FAVOURITES_PAGE_ROUTE:
      case SEARCH_PAGE_ROUTE: {
        if (hasMetadataPanel) {
          const { searchDefaultSections } = app;
          const entity = lastSelectedSearchItem?.object_type;
          panels.push(
            getMetadataPanelItem(
              entity,
              SEARCH_ASSET_METADATA_PANEL,
              lastSelectedSearchItem?.category ?? undefined,
              false,
              searchDefaultSections.includes(SEARCH_ASSET_METADATA_PANEL),
            ),
          );
        }

        if (roles.canReadSearchHistory && !entityId) {
          panels.push({
            title: 'Search History',
            key: SEARCH_HISTORY_PANEL,
            iconName: 'history',
            component: SearchHistoryPanel,
            props: {},
            color: COLOR_CODING.history,
            helpButton: { path: SEARCH_HISTORY, title: 'Search History' },
          });
        }

        break;
      }

      case COLLECTION_PAGE_ROUTE: {
        if (lastSelectedSearchItem && hasMetadataPanel) {
          const { searchDefaultSections } = app;
          const entity = lastSelectedSearchItem.object_type;

          panels.push(
            getMetadataPanelItem(
              entity,
              SEARCH_ASSET_METADATA_PANEL,
              lastSelectedSearchItem.category ?? undefined,
              false,
              searchDefaultSections.includes(SEARCH_ASSET_METADATA_PANEL),
            ),
          );
        }

        if (
          roles.canReadMetadataCategories &&
          roles.canReadMetadataViews &&
          roles.canReadMetadataFields &&
          roles.canReadMetadataValues
        ) {
          if (!lastSelectedSearchItem) {
            panels.push({
              title: 'Metadata',
              key: SEARCH_ASSET_METADATA_PANEL,
              iconName: 'Svg2022_uad_metadata',
              component: AssetMetadataPanel,
              props: {
                entity: OBJECT_TYPE_COLLECTIONS,
                categoryName: collection?.category ?? undefined,
              },
              side: 'right',
              color: COLOR_CODING.metadata,
              helpButton: { path: METADATA, title: 'Metadata' },
              disabled: !collection,
            });
          }

          if (userRoles.webEnablePosters) {
            panels.push({
              title: 'Cover Images',
              key: POSTERS_KEY,
              iconName: 'snapshot',
              component: EntityPostersPanel,
              side: 'right',
              color: COLOR_CODING.posters,
              props: {
                entity: OBJECT_TYPE_COLLECTIONS,
              },
              disabled: !collection,
            });
          }

          if (roles.canReadShares) {
            panels.push({
              title: 'Shares',
              key: COLLECTION_SHARES_PANEL,
              iconName: 'share',
              component: SharesPanel,
              props: {},
              side: 'right',
              minSize: 388,
              color: COLOR_CODING.shares,
              disabled: !collection,
              helpButton: { path: SHARES, title: 'Shares' },
            });
          }
        }

        if (roles.canReadAcls) {
          panels.push({
            title: 'Access control',
            key: COLLECTION_ACLS_PANEL,
            iconName: 'access_control',
            color: COLOR_CODING.acls,
            component: EntityAclsPanel,
            props: {
              objectId: entityId,
              aclsType: OBJECT_TYPE_COLLECTIONS,
              stickyForm: true,
            },
            side: 'right',
            defaultSize: 480,
            disabled: !collection,
            helpButton: { path: ACLS_HELP, title: 'Access control' },
          });
        }

        break;
      }

      case SAVED_SEARCH_PAGE_ROUTE: {
        if (hasMetadataPanel) {
          const { searchDefaultSections } = app;
          const entity = lastSelectedSearchItem?.object_type;
          panels.push(
            getMetadataPanelItem(
              entity,
              SEARCH_ASSET_METADATA_PANEL,
              lastSelectedSearchItem?.category ?? undefined,
              false,
              searchDefaultSections.includes(SEARCH_ASSET_METADATA_PANEL),
            ),
          );
        }

        if (roles.canReadAcls) {
          panels.push({
            title: 'Access control',
            key: SEARCH_SAVED_SEARCH_ACLS_PANEL,
            iconName: 'access_control',
            color: COLOR_CODING.acls,
            component: EntityAclsPanel,
            props: {
              objectId: entityId,
              aclsType: OBJECT_TYPE_SAVED_SEARCHES,
              stickyForm: true,
            },
            side: 'right',
            defaultSize: 480,
            disabled: !savedSearch,
          });
        }
        break;
      }

      default:
        break;
    }

    if (isCuttingRoomWritable) {
      const CUTTING_ROOM_DEFAULT_WIDTH = 800;

      panels.push({
        title: cuttingRoomProjectTitle
          ? `CuttingRoom – ${cuttingRoomProjectTitle}`
          : 'CuttingRoom Project',
        key: CUTTING_ROOM_PROJECT_PANEL,
        iconName: 'project',
        defaultSize: CUTTING_ROOM_DEFAULT_WIDTH,
        component: CuttingRoomProjectPanel,
        props: {},
        side: 'right',
        color: COLOR_CODING.cutting_room,
        canMaximize: true,
      });
    }

    if (
      !isSmallScreen &&
      canUpload &&
      !isMappedCollectionLocked &&
      !isArchived &&
      !isOnboarding
    ) {
      panels.push({
        title: 'Upload',
        key: UPLOADS_PANEL,
        iconName: 'load_up',
        component: UploadsWidgetPanel,
        props: {},
        color: COLOR_CODING.uploads,
        badge: getUploadingBadge(files),
        badgeColor: 'var(--color-validation-success-default)',
        bottom: true,
        widget: true,
      });
    }

    return panels;
  },
);
